/**
 * 통합 등록 다이얼로그 — 현장에서 뭔가를 남길 때 여는 **단 하나의** 진입점.
 *
 * 예전에는 남길 내용마다 버튼과 다이얼로그가 따로 있었다: "일정 등록", "메모
 * 등록", "출장 일지 추가", "문서 업로드". 화면마다 어떤 버튼이 있는지도 달라서,
 * 현장 담당자는 쓸 내용을 떠올린 뒤 "이건 어디서 등록하지" 를 매번 찾아야 했다.
 *
 * 여기서는 순서를 뒤집는다 — 등록을 열고, **종류를 고른다**. 종류를 잘못 골랐어도
 * 지금까지 쓴 내용은 따라간다(RecordCarry): 제목↔방문 목적, 내용↔작업 내용,
 * 담당자↔방문자, 시작↔방문일 은 이름만 다르고 담는 뜻이 같다.
 *
 * 저장 대상이 서로 다른 엔티티라 폼을 물리적으로 하나로 합칠 수는 없다
 * (site_schedules / site_visit_logs / site_documents). 그래서 이 컴포넌트는
 * 종류와 이어받을 값만 소유하고 실제 폼은 각 다이얼로그에 위임한다. 사용자가
 * 하나로 느끼게 하는 것은 공용 종류 선택기와 공용 폼 조각(formParts)이다.
 *
 * 수정은 이 진입점을 쓰지 않는다 — 저장된 일정을 출장 일지로 바꿀 수는 없으므로
 * (엔티티가 다르다) 각 다이얼로그를 직접 열어 종류 선택기를 감춘다.
 */
import React, { useEffect, useRef, useState } from 'react';

import ScheduleFormDialog from './ScheduleFormDialog';
import VisitLogFormDialog from './VisitLogFormDialog';
import DocumentUploadDialog from './DocumentUploadDialog';
import { RECORD_KINDS, RecordCarry, RecordKind } from './RecordKindSelector';
import { Project } from '../../models/Project';
import { ScheduleEventType, SiteSchedule } from '../../models/SiteSchedule';
import { SiteVisitLog } from '../../models/SiteVisitLog';

/** 저장 결과 — 호출 측이 목록 갱신과 스낵 문구를 결정하는 데 쓴다. */
export interface RecordSavedResult {
  kind: RecordKind;
  schedule?: SiteSchedule;
  visitLog?: SiteVisitLog | null;
  /** 파일 업로드 성공 건수. */
  uploadedCount?: number;
}

export interface RecordFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** 처음 열릴 때 선택돼 있을 종류. 화면마다 "가장 많이 쓰는 것" 을 지정한다. */
  initialKind?: RecordKind;
  /** 단일 현장 컨텍스트 (현장 상세 페이지). selectableSites 와 둘 중 하나. */
  projectId?: number;
  /** 단일 현장 컨텍스트에서 보여줄 현장 이름. */
  siteName?: string;
  /** 현장 선택 모드 (현장 목록 등 현장 무관 진입점). */
  selectableSites?: Project[];
  /** 캘린더에서 날짜를 눌러 들어온 경우의 날짜 프리셋. */
  presetStart?: Date | null;
  /** 캘린더에서 기간을 드래그해 들어온 경우의 종료 프리셋 — presetStart 와 함께 사용. */
  presetEnd?: Date | null;
  /** 일정으로 시작할 때의 초기 유형(출장/점검/…). */
  initialEventType?: ScheduleEventType;
  /** 노출할 종류. 기본은 4종 전부. */
  kinds?: RecordKind[];
  /** 저장 성공 후 호출 — 부모가 닫기/새로고침/스낵을 처리. */
  onSaved?: (result: RecordSavedResult) => void;
}

const RecordFormDialog: React.FC<RecordFormDialogProps> = ({
  open,
  onClose,
  initialKind = 'schedule',
  projectId,
  siteName,
  selectableSites,
  presetStart,
  presetEnd,
  initialEventType,
  kinds = RECORD_KINDS,
  onSaved,
}) => {
  const [kind, setKind] = useState<RecordKind>(initialKind);
  /**
   * 종류를 옮길 때 이어받는 값. 다이얼로그가 열려 있는 동안 유지되므로
   * 일정 → 파일 → 일정 처럼 돌아와도 쓴 내용이 살아 있다.
   */
  const [carry, setCarry] = useState<RecordCarry | null>(null);
  /**
   * 파일 업로드 성공 건수 — 닫을 때 한 번에 알린다.
   *
   * 다른 종류는 저장하면 곧바로 닫는 게 맞지만, 파일은 여러 개를 한 번에 올리고
   * 건별로 성공/실패가 갈린다. 업로드가 끝난 순간 부모가 닫아 버리면 무엇이
   * 실패했는지 볼 기회가 사라진다. 그래서 업로드 폼은 스스로 결과를 보여 주게
   * 두고, 사용자가 닫을 때 목록 새로고침을 알린다.
   */
  const uploadedRef = useRef(0);

  // 열 때마다 요청된 종류로 되돌린다 — 지난번에 고른 종류가 남아 있으면
  // "메모 쓰기" 로 열었는데 출장 일지 폼이 뜨는 일이 생긴다.
  useEffect(() => {
    if (!open) return;
    setKind(initialKind);
    setCarry(null);
    uploadedRef.current = 0;
  }, [open, initialKind]);

  const handleKindChange = (next: RecordKind, nextCarry?: RecordCarry) => {
    if (nextCarry) setCarry(nextCarry);
    setKind(next);
  };

  // 현장 선택 모드에서 이어받은 현장 — 파일 업로드는 자체 현장 목록을 쓰므로
  // id 만 넘겨 미리 골라 준다.
  const carriedSiteId = carry?.siteId ? Number(carry.siteId) : undefined;

  if (kind === 'file') {
    return (
      <DocumentUploadDialog
        open={open}
        onClose={() => {
          if (uploadedRef.current > 0) {
            onSaved?.({ kind: 'file', uploadedCount: uploadedRef.current });
          }
          onClose();
        }}
        projectId={projectId}
        projectLabel={siteName}
        initialSiteId={Number.isFinite(carriedSiteId) ? carriedSiteId : undefined}
        availableKinds={kinds}
        onKindChange={(k) => handleKindChange(k)}
        onUploaded={(count) => {
          uploadedRef.current += count;
        }}
      />
    );
  }

  if (kind === 'visitlog') {
    return (
      <VisitLogFormDialog
        open={open}
        onClose={onClose}
        projectId={projectId}
        siteName={siteName}
        selectableSites={selectableSites}
        editing={null}
        // 캘린더 날짜 클릭으로 들어온 프리셋은 종류가 달라도 같은 뜻이다 —
        // 일정의 시작일 = 출장 일지의 방문일.
        defaultVisitedAt={presetStart ? presetStart.toISOString() : undefined}
        availableKinds={kinds}
        onKindChange={handleKindChange}
        initialCarry={carry}
        onSaved={(log) => onSaved?.({ kind: 'visitlog', visitLog: log })}
      />
    );
  }

  return (
    <ScheduleFormDialog
      open={open}
      editing={null}
      onClose={onClose}
      projectId={projectId}
      siteName={siteName}
      selectableSites={selectableSites}
      presetStart={presetStart}
      presetEnd={presetEnd}
      // 'memo' 로 열면 메모 모드로 시작한다. 일정이면 화면이 지정한 초기 유형.
      initialEventType={kind === 'memo' ? 'memo' : initialEventType}
      availableKinds={kinds}
      onKindChange={handleKindChange}
      initialCarry={carry}
      onSaved={(saved) => onSaved?.({ kind: saved.event_type === 'memo' ? 'memo' : 'schedule', schedule: saved })}
    />
  );
};

export default RecordFormDialog;
